<template>
  <bm-dialog
    :visible="value"
    :label="$m.production_packaging_assembly_print_labels"
    @accept="submit"
    @cancel="cancel"
  >
    <v-container pa-0 fluid>
      <!-- Only ask for stock item if we don't have one -->
      <v-row v-if="!stockItem">
        <v-col>
          <bm-dropdown
            v-model="selectedItem"
            :t="$m.keys.enum_noun_item"
            :items="items"
            item-text="details.name"
            return-object
          />
        </v-col>
      </v-row>
      <!-- Only ask for a URN if we don't have one -->
      <div v-if="selectedItem">
        <v-row>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-text
              v-model="newBatchUrn"
              :t="$m.keys.assembly_batch_number"
              :rules="[$ruleSet.required()]"
              :disabled="!!batchUrn"
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-dropdown
              v-model="newBatchUrn"
              :t="$m.keys.assembly_batch_number"
              :items="itemStockBatches"
              item-text="urn"
              item-value="urn"
              allow-custom-value
            />
          </v-col>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-date
              v-model="newBestBefore"
              :t="$m.keys.assembly_best_before_date"
              clearable
            />
          </v-col>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-date
              v-model="newPackagedOn"
              :t="$m.keys.container_label_packaged_on"
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-date
              v-model="newUseByDate"
              :t="$m.keys.stock_use_by_date"
              clearable
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-date
              v-model="newCreationDate"
              :t="$m.keys.stock_creation_date"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            {{ $m.production_select_number_labels }}
          </v-col>
        </v-row>
        <v-row v-if="showNonReturnable">
          <v-col :cols="8">
            <bm-dropdown
              v-if="nonReturnableLabel"
              v-model="nonReturnableLabel.labelDocumentTemplateId"
              :items="nonReturnableTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number
              v-model="nonReturnableLabel.quantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
            >
            </bm-number>
          </v-col>
        </v-row>
        <v-row v-if="showReturnable">
          <v-col :cols="8">
            <bm-dropdown
              v-model="returnableLabel.labelDocumentTemplateId"
              :items="returnableTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number
              v-model="returnableLabel.quantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
            >
            </bm-number>
          </v-col>
        </v-row>
        <v-row v-if="showMaterialLabel">
          <v-col :cols="8">
            <bm-dropdown
              v-model="materialLabel.labelDocumentTemplateId"
              :items="materialLabelTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number
              v-model="materialLabel.quantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
            >
            </bm-number>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <template #controls-confirm-text>
      {{ $m.production_print_labels }}
    </template>
  </bm-dialog>
</template>

<script lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import { useReactiveDialogSetup } from "@/components/mixins/dialogs";
import api from "@/app/api";
import {
  containerLabelService,
  LabelTemplateAndQuantity,
} from "@/app/services/generated/containerlabelService";
import type { DocumentTemplate } from "@/app/services/generated/documenttemplateService";
import { documentTemplateService } from "@/app/services/generated/documenttemplateService";
import { EnumDocumentType } from "@/app/services/generated/enums/enumDocumentType.generated";
import Guid from "@/app/types/common/guid";
import type { PropType } from "vue";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import {
  makeReactive,
  makeDeepRef,
  withLoading,
} from "@/components/composables/componentTools";
import type { LocalDate } from "@js-joda/core";
import { translateWithParams, useI18n } from "@/plugins/i18n";
import { initiateDownloadFromBytes, toGroupedGuidMap } from "@/app/apiutils";
import { EnumDocumentFileType } from "@/app/services/generated/enums/enumDocumentFileType.generated";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { StockBatch } from "@/app/services/generated/stockbatchService";
import { stockBatchService } from "@/app/services/generated/stockbatchService";
import { EnumYesNoEither } from "@/app/services/generated/enums/enumYesNoEither.generated";
import { itemDocumentService } from "@/app/services/generated/itemdocumentService";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { Item } from "@/app/services/generated/itemService";
import apiCache from "@/app/caching/apiCache";
import ItemsCache from "@/app/caching/caches/itemCache";
import PackageTypesCache from "@/app/caching/caches/packageTypesCache";
import {
  itemBestBeforeDate,
  itemIsReturnable,
  itemIsTraceable,
  itemPackageType,
} from "@/business_logic/stock/itemUtils";
import { GuidMap } from "@/app/types/common/guidcollections";

export interface PrintLabelsEvent {
  nonReturnableLabel?: LabelTemplateAndQuantity;
  returnableLabel?: LabelTemplateAndQuantity;
  materialLabel?: LabelTemplateAndQuantity;
  newBatchUrn: string;
  newBestBefore: LocalDate;
  newUseByDate: LocalDate;
  newCreationDate: LocalDate;
  newPackagedOn: LocalDate;
}

/**
 * Dialog will automatically download labels if requested - use event if there are further actions that need to be taken.
 *
 * accept PrintLabelsEvent
 */
export default defineComponent({
  name: "PrintLabelsDialog",
  components: { BmDialog },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    stockItem: {
      type: Object as PropType<Item | null>,
      required: false,
      default: () => null,
    },
    rackedQuantity: {
      type: Number,
      required: false,
      default: 0,
    },
    producedQuantity: {
      type: Number,
      required: false,
      default: 0,
    },
    batchUrn: {
      type: String,
      required: false,
      default: "",
    },
    bestBefore: {
      type: Object as PropType<LocalDate | null>,
      required: false,
      default: () => null,
    },
    useByDate: {
      type: Object as PropType<LocalDate>,
      required: false,
    },
    creationDate: {
      type: Object as PropType<LocalDate>,
      required: false,
      default: () => tenantService.today,
    },
    packagedOn: {
      type: Object as PropType<LocalDate>,
      required: false,
      default: () => tenantService.today,
    },
    processSubBatch: {
      type: String,
      required: false,
      default: "",
    },
  },
  setup(props, context) {
    const { $t, $m } = useI18n();
    const returnableLabel = makeReactive(new LabelTemplateAndQuantity());
    returnableLabel.labelDocumentType = EnumDocumentType.returnableLabel;
    const nonReturnableLabel = makeReactive(new LabelTemplateAndQuantity());
    nonReturnableLabel.labelDocumentType = EnumDocumentType.nonReturnableLabel;
    const materialLabel = makeReactive(new LabelTemplateAndQuantity());
    materialLabel.labelDocumentType = EnumDocumentType.materialLabel;

    const nonReturnableTemplates = ref<DocumentTemplate[]>([]);
    const returnableTemplates = ref<DocumentTemplate[]>([]);
    const materialLabelTemplates = ref<DocumentTemplate[]>([]);

    const cache = apiCache.buildCacheSet({
      items: ItemsCache,
      packageTypes: PackageTypesCache,
    });
    const items = makeRef<readonly Item[]>([]);
    const selectedItem = makeRef(props.stockItem);

    const valid = ref(true);
    const { cancel, accept } = useReactiveDialogSetup(
      ref(props.value),
      context
    );

    const newBatchUrn = ref(props.batchUrn);
    const newBestBefore = makeRef(props.bestBefore);
    const newUseByDate = makeDeepRef(props.useByDate);
    const newCreationDate = makeDeepRef(props.creationDate);
    const newPackagedOn = makeDeepRef(props.packagedOn);

    const stockBatchesPerItem = makeRef<GuidMap<StockBatch[]>>(new GuidMap());

    const selectedItemId = computed(() => selectedItem.value?.details.id);
    const packageType = computed(() =>
      itemPackageType(selectedItem.value, cache)
    );
    const isMaterial = computed(
      () => selectedItem.value?.details.mainPurpose.isMaterial() ?? false
    );
    const itemStockBatches = computed(
      () => stockBatchesPerItem.value.get(selectedItemId.value) ?? []
    );

    const submit = withLoading(async () => {
      // No labels selected = cancel
      if (
        returnableLabel.quantity +
          nonReturnableLabel.quantity +
          materialLabel.quantity ===
          0 ||
        !selectedItemId.value
      ) {
        cancel();
        return;
      }

      let filename = translateWithParams("production_label_template_pdf", {
        key: newBatchUrn.value,
      }).toString();

      await initiateDownloadFromBytes(filename, "application/pdf", async () => {
        if (showMaterialLabel.value) {
          return await itemDocumentService.generateMaterialLabel(
            api.tenantId,
            selectedItemId.value!,
            EnumDocumentFileType.pdf,
            materialLabel.quantity,
            newUseByDate.value ?? null,
            newCreationDate.value,
            newBatchUrn.value,
            materialLabel.labelDocumentTemplateId
          );
        } else {
          return await containerLabelService.generateContainerLabels(
            api.tenantId,
            selectedItemId.value!,
            newBatchUrn.value,
            newBestBefore.value,
            EnumDocumentFileType.pdf,
            newPackagedOn.value,
            props.processSubBatch,
            [returnableLabel, nonReturnableLabel]
          );
        }
      });

      accept({
        returnableLabel: returnableLabel,
        nonReturnableLabel: nonReturnableLabel,
        materialLabel: materialLabel,
        newBatchUrn: newBatchUrn.value,
        newBestBefore: newBestBefore.value!,
        newUseByDate: newUseByDate.value,
        newCreationDate: newCreationDate.value,
        newPackagedOn: newPackagedOn.value,
      } as PrintLabelsEvent);
    });

    const loadPrerequisites = withLoading(async () => {
      await cache.update();

      let allTemplates = await documentTemplateService.getAllDocumentTemplates(
        api.tenantId
      );

      nonReturnableTemplates.value = allTemplates.filter(
        (template) =>
          template.documentType === EnumDocumentType.nonReturnableLabel
      );
      returnableTemplates.value = allTemplates.filter(
        (template) => template.documentType === EnumDocumentType.returnableLabel
      );
      materialLabelTemplates.value = allTemplates.filter(
        (template) => template.documentType === EnumDocumentType.materialLabel
      );

      let defaultNonReturnableTemplate = nonReturnableTemplates.value.find(
        (nrt) => nrt.isDocumentTypeDefault
      );

      let defaultReturnableTemplate = returnableTemplates.value.find(
        (rt) => rt.isDocumentTypeDefault
      );

      let defaultMaterialTemplate = materialLabelTemplates.value.find(
        (mt) => mt.isDocumentTypeDefault
      );

      let nonReturnableQuantity = 0;
      let returnableQuantity = 0;

      // If We have produced more than we have racked we take the Delta into the Non Returnables as a suggestion
      if (packageType.value) {
        if (packageType.value.isReturnable) {
          if (
            props.rackedQuantity &&
            props.rackedQuantity < props.producedQuantity
          ) {
            nonReturnableQuantity =
              props.producedQuantity - props.rackedQuantity;
            returnableQuantity = props.rackedQuantity;
          } else {
            returnableQuantity = props.producedQuantity;
          }
        } else if (packageType.value.isTraceable) {
          nonReturnableQuantity = props.producedQuantity;
        }
      }

      nonReturnableLabel.labelDocumentTemplateId =
        packageType.value?.defaultNonReturnableLabelDocumentTemplateId ??
        defaultNonReturnableTemplate?.id ??
        nonReturnableTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
        Guid.createEmpty();
      nonReturnableLabel.quantity = nonReturnableQuantity;

      returnableLabel.labelDocumentTemplateId =
        packageType.value?.defaultReturnableLabelDocumentTemplateId ??
        defaultReturnableTemplate?.id ??
        returnableTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
        Guid.createEmpty();
      returnableLabel.quantity = returnableQuantity;

      let materialLabelQuantity = 1;

      materialLabel.labelDocumentTemplateId =
        defaultMaterialTemplate?.id ??
        materialLabelTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
        Guid.createEmpty();
      materialLabel.quantity = materialLabelQuantity;

      items.value = cache.items.all.filter(
        (item) =>
          item.details.mainPurpose.isMaterial() ||
          (item.details.mainPurpose.isProduct() &&
            (itemIsTraceable(item, cache) || itemIsReturnable(item, cache)))
      );

      stockBatchesPerItem.value = toGroupedGuidMap(
        await stockBatchService.getStockBatchesForItems(
          api.tenantId,
          items.value.map((x) => x.details.id),
          EnumYesNoEither.no
        ),
        (x) => x.stockItemId
      );

      newBatchUrn.value = isMaterial.value
        ? itemStockBatches.value[0]?.urn
        : props.batchUrn;

      evaluateBestBeforeDate();
    });

    const evaluateBestBeforeDate = () => {
      if (props.bestBefore) {
        newBestBefore.value = props.bestBefore;
      } else if (selectedItemId.value) {
        newBestBefore.value = itemBestBeforeDate(
          cache.items.getById(selectedItemId.value)
        );
      }
    };

    watch(selectedItem, () => evaluateBestBeforeDate());

    const showNonReturnable = computed(
      () =>
        nonReturnableTemplates.value.length &&
        itemIsTraceable(selectedItem.value, cache)
    );

    const showReturnable = computed(
      () =>
        returnableTemplates.value.length &&
        itemIsReturnable(selectedItem.value, cache)
    );

    const showMaterialLabel = computed(
      () => materialLabelTemplates.value.length && isMaterial.value
    );

    onMounted(withLoading(loadPrerequisites));

    return {
      valid,
      returnableTemplates,
      nonReturnableTemplates,
      materialLabelTemplates,
      returnableLabel,
      nonReturnableLabel,
      materialLabel,
      cancel,
      submit,
      showNonReturnable,
      showReturnable,
      showMaterialLabel,
      newBatchUrn,
      newBestBefore,
      newUseByDate,
      newCreationDate,
      newPackagedOn,
      itemStockBatches,
      selectedItem,
      items,
    };
  },
});
</script>
